/**
 * Plantillas de las 8 clases de pista, con 2–3 variantes por clase para que dos
 * pistas del mismo tipo no se lean igual.
 *
 * Cada variante es una función y no una cadena con huecos: la concordancia
 * («el primero» / «la primera», «al Sótano» / «a las Caballerizas») se resuelve con
 * los helpers de `forms.ts` y el compilador garantiza que no queda hueco sin
 * rellenar. Toda pista cabe en una línea.
 */

import type { ClueKind } from '@murdoku/engine';
import type { Gender, NounForms } from './forms';
import { firstOrdinal, lastOrdinal } from './forms';

export interface PersonRef {
  name: string;
  gender: Gender;
}

/** Contexto de una pista ya resuelto a nombres y formas. */
export interface ClueContext {
  /** Persona principal (o la primera en JUST_BEFORE/BEFORE). */
  a: PersonRef;
  /** Segunda persona; solo en JUST_BEFORE y BEFORE. */
  b?: PersonRef;
  /** Etiqueta de hora ("23:00"); solo en AT y NOT_AT. */
  hour?: string;
  /** Formas de la sala con artículo y contracciones. */
  room: NounForms;
  /** "la noche", "la jornada", "el día"… con artículo. */
  period: string;
}

export type ClueTemplate = (ctx: ClueContext) => string;

function needs<T>(value: T | undefined, what: string): T {
  if (value === undefined) throw new Error(`plantilla sin ${what} en el contexto`);
  return value;
}

/**
 * Variantes por clase. El orden importa: `renderClues` las rota en round-robin, así
 * que la variante 0 es la formulación canónica de cada clase.
 */
export const CLUE_TEMPLATES: Record<ClueKind, readonly ClueTemplate[]> = {
  AT: [
    (c) => `A las ${needs(c.hour, 'hora')}, ${c.a.name} estaba ${c.room.en}.`,
    (c) => `A las ${needs(c.hour, 'hora')} se vio a ${c.a.name} ${c.room.en}.`,
    (c) => `Consta que a las ${needs(c.hour, 'hora')} ${c.a.name} estaba ${c.room.en}.`,
  ],
  NOT_AT: [
    (c) => `A las ${needs(c.hour, 'hora')}, ${c.a.name} no estaba ${c.room.en}.`,
    (c) => `Nadie vio a ${c.a.name} ${c.room.en} a las ${needs(c.hour, 'hora')}.`,
    (c) =>
      `${c.a.name} puede probar que a las ${needs(c.hour, 'hora')} no pisó ${c.room.def}.`,
  ],
  OPEN: [
    (c) => `${c.a.name} abrió ${c.period} ${c.room.en}.`,
    (c) => `A primera hora, ${c.a.name} estaba ${c.room.en}.`,
    (c) => `La primera hora encontró a ${c.a.name} ${c.room.en}.`,
  ],
  CLOSE: [
    (c) => `${c.a.name} terminó ${c.period} ${c.room.en}.`,
    (c) => `A última hora, ${c.a.name} estaba ${c.room.en}.`,
    (c) => `${c.a.name} acabó ${c.room.en}.`,
  ],
  FIRST_IN: [
    (c) => `${c.a.name} fue ${firstOrdinal(c.a.gender)} en entrar ${c.room.en}.`,
    (c) => `Nadie pisó ${c.room.def} antes que ${c.a.name}.`,
    (c) => `${c.a.name} fue quien estrenó ${c.room.def}.`,
  ],
  LAST_IN: [
    (c) => `${c.a.name} fue ${lastOrdinal(c.a.gender)} en pisar ${c.room.def}.`,
    (c) => `Nadie entró ${c.room.en} después de ${c.a.name}.`,
    (c) => `El paso de ${c.a.name} por ${c.room.def} fue el último de ${c.period}.`,
  ],
  JUST_BEFORE: [
    (c) =>
      `${c.a.name} pasó por ${c.room.def} justo antes que ${needs(c.b, 'persona B').name}.`,
    (c) =>
      `${needs(c.b, 'persona B').name} entró ${c.room.en} justo después de ${c.a.name}.`,
    (c) =>
      `Cuando ${needs(c.b, 'persona B').name} llegó ${c.room.al}, ${c.a.name} acababa de irse.`,
  ],
  BEFORE: [
    (c) => `${c.a.name} estuvo ${c.room.en} antes que ${needs(c.b, 'persona B').name}.`,
    (c) =>
      `Cuando ${needs(c.b, 'persona B').name} entró ${c.room.en}, ${c.a.name} ya había pasado por allí.`,
    (c) =>
      `${c.a.name} anduvo por ${c.room.def} antes que ${needs(c.b, 'persona B').name}.`,
  ],
};
